import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress, Chip, IconButton, Tooltip,
  Autocomplete, FormControl, InputLabel, Select, MenuItem, Tabs, Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import authService from '../../services/authService';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Mapping {
  lab_name: string;
  canonical_name: string;
  mapping_type: string;
  combo_strategy: string;
  notes: string | null;
}

const OrganismMapping: React.FC = () => {
  const [labs, setLabs] = useState<any[]>([]);
  const [selectedLabId, setSelectedLabId] = useState('');
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [canonicalOrganisms, setCanonicalOrganisms] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [mappingId, setMappingId] = useState('');
  const [systemCount, setSystemCount] = useState(0);

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ lab_name: '', canonical_name: '', combo_strategy: 'single', notes: '' });

  // Bulk upload dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');

  // Tab
  const [tab, setTab] = useState(0);

  const getHeaders = () => authService.getAuthHeaders();

  const fetchLabs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/lis-portal/labs`, { headers: getHeaders() });
      if (res.data.success) {
        setLabs(res.data.labs || []);
        if (res.data.labs?.length > 0 && !selectedLabId) {
          setSelectedLabId(res.data.labs[0].lab_id);
        }
      }
    } catch (err) {
      setError('Failed to fetch labs');
    }
  };

  const fetchMappings = async () => {
    if (!selectedLabId) return;
    try {
      setLoading(true);
      const params: any = {};
      if (search) params.search = search;
      const res = await axios.get(`${API_URL}/api/v1/lis-portal/organism-mapping/${selectedLabId}`, { headers: getHeaders(), params });
      if (res.data.success) {
        setMappings(res.data.mappings || []);
        setMappingId(res.data.mapping_id || '');
        setSystemCount(res.data.system_count || 0);
      }
    } catch (err) {
      setError('Failed to fetch mappings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCanonical = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/lis-portal/organism-mapping/canonical`, { headers: getHeaders() });
      if (res.data.success) {
        setCanonicalOrganisms(res.data.organisms || []);
      }
    } catch (err) { /* silent */ }
  };

  useEffect(() => { fetchLabs(); fetchCanonical(); }, []);
  useEffect(() => { if (selectedLabId) fetchMappings(); }, [selectedLabId]);

  const handleAdd = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/v1/lis-portal/organism-mapping/${selectedLabId}`, addForm, { headers: getHeaders() });
      if (res.data.success) {
        setSuccess(res.data.message);
        setAddOpen(false);
        setAddForm({ lab_name: '', canonical_name: '', combo_strategy: 'single', notes: '' });
        fetchMappings();
      } else {
        setError(res.data.error);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add mapping');
    }
  };

  const handleDelete = async (labName: string) => {
    if (!window.confirm(`Delete mapping for "${labName}"?`)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/v1/lis-portal/organism-mapping/${selectedLabId}/${encodeURIComponent(labName)}`, { headers: getHeaders() });
      if (res.data.success) {
        setSuccess(res.data.message);
        fetchMappings();
      }
    } catch (err) {
      setError('Failed to delete mapping');
    }
  };

  const handleBulkUpload = async () => {
    try {
      const lines = bulkText.trim().split('\n').filter(l => l.includes(','));
      const mappingsData = lines.map(line => {
        const [lab_name, canonical_name] = line.split(',').map(s => s.trim());
        return { lab_name, canonical_name };
      });

      const res = await axios.post(`${API_URL}/api/v1/lis-portal/organism-mapping/${selectedLabId}/bulk`, { mappings: mappingsData }, { headers: getHeaders() });
      if (res.data.success) {
        setSuccess(`Bulk upload: ${res.data.added} added, ${res.data.skipped} skipped`);
        setBulkOpen(false);
        setBulkText('');
        fetchMappings();
      }
    } catch (err) {
      setError('Bulk upload failed');
    }
  };

  const handleSearch = () => { fetchMappings(); };

  const handleDownloadTemplate = async () => {
    // Fetch all canonical names to include in template
    let allCanonical = canonicalOrganisms;
    if (!allCanonical.length) {
      try {
        const res = await axios.get(`${API_URL}/api/v1/lis-portal/organism-mapping/canonical`, { headers: getHeaders() });
        if (res.data.success) allCanonical = res.data.organisms || [];
      } catch (err) { /* use empty */ }
    }

    const lines: string[] = [];
    lines.push('lab_name,canonical_name');
    lines.push('# ───── EXAMPLES (replace with your LIS names) ─────');
    lines.push('K. pneumoniae,Klebsiella pneumoniae / Klebsiella oxytoca');
    lines.push('E COLI,Escherichia coli');
    lines.push('STAPH AUREUS,Staphylococcus aureus');
    lines.push('');
    lines.push('# ───── OUR STANDARDIZED NAMES (use these in canonical_name column) ─────');
    for (const name of allCanonical) {
      lines.push(`# ${name}`);
    }

    const csv = lines.join('\n') + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'organism_mapping_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCurrent = () => {
    if (!mappings.length) return;
    const header = 'lab_name,canonical_name\n';
    const rows = mappings.map(m => `${m.lab_name},${m.canonical_name}`).join('\n');
    const csv = header + rows + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `organism_mappings_${selectedLabId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const lines = text.trim().split('\n');
      // Skip header, comments (#), and empty lines
      const dataLines = lines.filter(l => {
        const trimmed = l.trim();
        return trimmed && !trimmed.startsWith('#') && !trimmed.toLowerCase().startsWith('lab_name') && trimmed.includes(',');
      });
      setBulkText(dataLines.join('\n'));
    };
    reader.readAsText(file);
    // Reset input so same file can be re-uploaded
    e.target.value = '';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Organism Name Mapping</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} size="small">
            Download Template
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadCurrent} size="small"
            disabled={!mappings.length}>
            Export Current
          </Button>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setBulkOpen(true)} size="small">
            Bulk Upload
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} size="small">
            Add Mapping
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Lab selector + search */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Lab</InputLabel>
            <Select value={selectedLabId} label="Lab" onChange={(e) => setSelectedLabId(e.target.value)}>
              {labs.map(l => <MenuItem key={l.lab_id} value={l.lab_id}>{l.name} ({l.lab_id})</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" placeholder="Search organisms..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 250 }} />
          <Button variant="outlined" startIcon={<SearchIcon />} onClick={handleSearch}>Search</Button>
          <Box sx={{ ml: 'auto' }}>
            <Chip label={`Mapping: ${mappingId}`} size="small" color="primary" sx={{ mr: 1 }} />
            <Chip label={`${mappings.length} lab mappings`} size="small" />
            <Chip label={`${systemCount} system defaults`} size="small" sx={{ ml: 0.5 }} variant="outlined" />
          </Box>
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Map your LIS organism names to our standardized names. When your system sends a name we don't recognize, add it here.
      </Typography>

      {/* Mappings table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Your LIS Name</strong></TableCell>
                <TableCell><strong>Our Standardized Name</strong></TableCell>
                <TableCell align="center">Strategy</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No mappings found. Add your first mapping or upload a CSV.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                mappings.map((m) => (
                  <TableRow key={m.lab_name} hover>
                    <TableCell><strong>{m.lab_name}</strong></TableCell>
                    <TableCell>
                      <Chip label={m.canonical_name} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={m.combo_strategy} size="small" color={m.combo_strategy === 'expand' ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell>{m.notes || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleDelete(m.lab_name)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Mapping Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Organism Mapping</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Map a name from your LIS to our standardized organism name.
          </Typography>
          <TextField label="Your LIS Organism Name" fullWidth margin="normal" required
            placeholder="e.g. K. pneumoniae, E COLI, STAPH AUREUS"
            value={addForm.lab_name} onChange={(e) => setAddForm({ ...addForm, lab_name: e.target.value })} />
          <Autocomplete
            freeSolo
            options={canonicalOrganisms}
            value={addForm.canonical_name}
            onChange={(_, val) => setAddForm({ ...addForm, canonical_name: val || '' })}
            onInputChange={(_, val) => setAddForm({ ...addForm, canonical_name: val || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Our Standardized Name" fullWidth margin="normal" required
                placeholder="Start typing to search..." />
            )}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Strategy</InputLabel>
            <Select value={addForm.combo_strategy} label="Strategy"
              onChange={(e) => setAddForm({ ...addForm, combo_strategy: e.target.value })}>
              <MenuItem value="single">Single (1:1 mapping)</MenuItem>
              <MenuItem value="expand">Expand (1:many — name maps to multiple organisms)</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Notes (optional)" fullWidth margin="normal"
            value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}
            disabled={!addForm.lab_name || !addForm.canonical_name}>
            Add Mapping
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Upload Mappings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a CSV file or paste data. Two columns: <strong>lab_name, canonical_name</strong>.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
              Upload CSV File
              <input type="file" hidden accept=".csv,.txt" onChange={handleCsvFileUpload} />
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
              Download Template
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            CSV format (header row optional):<br />
            <code>lab_name,canonical_name</code><br />
            <code>K. pneumoniae,Klebsiella pneumoniae / Klebsiella oxytoca</code><br />
            <code>E COLI,Escherichia coli</code>
          </Alert>

          <TextField
            fullWidth multiline rows={10}
            placeholder="lab_name,canonical_name&#10;K. pneumoniae,Klebsiella pneumoniae / Klebsiella oxytoca&#10;E COLI,Escherichia coli"
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            label={bulkText ? `${bulkText.trim().split('\n').filter(l => l.includes(',')).length} mappings detected` : 'Paste or upload CSV data'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulkOpen(false); setBulkText(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkUpload}
            disabled={!bulkText.trim() || !bulkText.includes(',')}>
            Upload {bulkText.trim().split('\n').filter(l => l.includes(',')).length} Mappings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganismMapping;
